// Production calendar, Talent calendar tab (DESIGN rev 2.1 §2.6 Calendar cell). Booking model (owner call
// 2026-09-01): each day lists AVAILABLE talent (gold, the warn tone) and BOOKED talent (sage, the ok tone: an explicit
// booking or a shoot segment that day). Click a day to colour the talent pool for it; double-click it (or its
// "Shootable scripts" button) for the scripts that day's booked talent can shoot. Producers book and unbook from a
// name, edit a talent's availability, and keep a day note. Same calls as before (listed in Calendar.jsx).
import { useState, useEffect, useCallback, useMemo } from 'react';
import { CalendarCheck, CalendarClock, Check, ChevronLeft, ChevronRight, ListChecks, Pencil, StickyNote, Trash2, TriangleAlert } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import CalendarCell from '../../ui/CalendarCell';
import ErrorState from '../../ui/ErrorState';
import Icon from '../../ui/Icon';
import Input from '../../ui/Input';
import Radio from '../../ui/Radio';
import Select from '../../ui/Select';
import Skeleton from '../../ui/Skeleton';
import StageChip from '../../ui/StageChip';
import Tag from '../../ui/Tag';
import Textarea from '../../ui/Textarea';
import { useConfirm } from '../../ui/Confirm';
import { useToast } from '../../ui/Toast';
import { stageOf } from '../../ui/stages';
import { cx } from '../../ui/util';
import { clean } from '../../shell/copy';
import TalentAvailabilityView, { AvailabilityModal } from '../../components/TalentAvailabilityView';
import Modal from '../../ui/Modal';
import { FoldControl, useExpandedDays } from './ShootSchedule';
import { DAY_NAMES, MONTH_NAMES, buildCalDays, canAssignTalent, cellClick, dateKey, fmtTime12, plural, prettyDate } from './util';

const MAX_TALENT_CHIPS = 5;
const windowOf = av => (av.availabilityType === 'full_day' ? 'All day' : `${fmtTime12(av.blockStart)} to ${fmtTime12(av.blockEnd)}`);

// A talent's day as a chip: tone ok = booked or shooting, warn = available, danger = not available.
function TalentChip({ name, tag, tone, title, onClick }) {
  return (
    <span className="ui-chip ui-chip--static" data-tone={tone} title={title}>
      {onClick ? (
        <button type="button" className="ui-chip__main" onClick={e => { e.stopPropagation(); onClick(); }}>
          <span className="ui-chip__title">{name}</span>{tag && <span className="ui-chip__tag">{tag}</span>}
        </button>
      ) : (
        <span className="ui-chip__main"><span className="ui-chip__title">{name}</span>{tag && <span className="ui-chip__tag">{tag}</span>}</span>
      )}
    </span>
  );
}

// ── The segments a talent shoots on a day (read only) ─────────────────────────
function TalentShootsDialog({ talent, dateStr, segments, loading, onClose }) {
  const who = clean(talent.name);
  return (
    <Modal title={`${who}: scheduled shoots`} meta={prettyDate(dateStr)} onClose={onClose}>
      {loading ? (
        <div className="ui-stack" role="status" aria-label="Loading the shoots"><Skeleton width="70%" /><Skeleton width="50%" /></div>
      ) : segments.length === 0 ? (
        <p className="ui-p">No shoots scheduled for {who} on this day.</p>
      ) : (
        <div className="ui-region ui-list">
          {segments.map(seg => {
            const lp = seg.liveProduction;
            const meta = [clean(seg.segmentLabel), clean(lp?.client?.name), clean(seg.setting)].filter(Boolean).join(', ');
            return (
              <div key={seg.id} className="ui-row">
                <span className="ui-row__main">
                  <span className="ui-row__title">{clean(lp?.script?.scriptName) || 'Untitled'}</span>
                  {meta && <span className="ui-row__meta">{meta}</span>}
                </span>
                <span className="ui-row__trail">
                  {lp?.status ? <StageChip stage={stageOf({ cardStatus: lp.status })} /> : <span className="ui-meta">No status</span>}
                  {lp?.status === 'Incomplete' && <Badge condition="Incomplete" />}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ── Schedule a matched script on the day: all day, or at a time (2026-09-02 overhaul) ──
function ScheduleShootDialog({ row, dateStr, onDone, onClose }) {
  const [allDay, setAllDay] = useState(true);
  const [time,   setTime]   = useState('12:00');
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState('');
  const [avail,  setAvail]  = useState(null);   // the day's availability rows

  // The day's windows, so a time outside a talent's window warns BEFORE committing (2026-09-03, owner call).
  useEffect(() => {
    api.get(`/api/talent-availability/by-date?date=${dateStr}`).then(r => setAvail(r.data || [])).catch(() => setAvail([]));
  }, [dateStr]);

  const timeWarnings = useMemo(() => {
    if (allDay || !time || !avail) return [];
    const t = time.slice(0, 5);
    const seenIds = new Set();
    const warnings = [];
    for (const seg of row.segments) {
      if (!seg.shootable) continue;
      for (const tal of seg.talents) {
        if (!tal.talentId || seenIds.has(tal.talentId)) continue;
        seenIds.add(tal.talentId);
        const av = avail.find(a => a.talentId === tal.talentId);
        if (!av || av.availabilityType === 'full_day') continue;   // booked without an availability row: nothing to check
        const start = (av.blockStart || '').slice(0, 5);
        const end   = (av.blockEnd   || '').slice(0, 5);
        if (start && end && (t < start || t >= end)) warnings.push(`${clean(tal.name)}'s window is ${fmtTime12(start)} to ${fmtTime12(end)}`);
      }
    }
    return warnings;
  }, [allDay, time, avail, row.segments]);

  async function handleSchedule() {
    setBusy(true);
    setErr('');
    try {
      // One PATCH per shootable segment: the Scheduler panel's endpoint, so status sync and the date cache stay right.
      for (const segId of row.shootableSegmentIds) {
        await api.patch(`/api/shoot-segments/${segId}`, { shootDay: dateStr, shootTimeSlot: allDay ? null : time });
      }
      onDone();
    } catch (e) {
      setErr(e.response?.data?.error || 'Scheduling failed. Try again.');
      setBusy(false);
    }
  }

  const how = row.availableCount === row.totalUnfilmed
    ? `all ${row.totalUnfilmed === 1 ? 'segment' : `${row.totalUnfilmed} segments`}`
    : `${row.availableCount} of ${row.totalUnfilmed} segments`;
  return (
    <Modal title={`Schedule ${clean(row.title)}`} meta={`${prettyDate(dateStr)}: ${how} will be scheduled`} onClose={onClose}
      footer={(
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={Check} onClick={handleSchedule} loading={busy} disabled={!allDay && !time}>
            {timeWarnings.length ? 'Schedule anyway' : 'Schedule'}
          </Button>
        </>
      )}>
      <div className="ui-stack ui-stack--tight" role="radiogroup" aria-label="When">
        <Radio name="when" label="All day: shoot whenever the talent is in" checked={allDay} onChange={() => setAllDay(true)} />
        <div className="ui-cluster">
          <Radio name="when" label="At a time" checked={!allDay} onChange={() => setAllDay(false)} />
          {!allDay && <Input type="time" aria-label="Shoot time" value={time} onChange={e => setTime(e.target.value)} />}
        </div>
      </div>
      {timeWarnings.length > 0 && (
        <div className="ui-stack ui-stack--tight" role="alert">
          {timeWarnings.map((w, i) => <p key={i} className="ui-p ui-cluster ui-dense ui-text-danger"><Icon icon={TriangleAlert} />{w}</p>)}
          <p className="ui-p ui-meta">You can still schedule. The shoot shows as a conflict until the time or their availability changes.</p>
        </div>
      )}
      {err && <p className="ui-p ui-field__error" role="alert">{clean(err)}</p>}
    </Modal>
  );
}

// ── Shootable scripts on a day (double-click a day) ───────────────────────────
// Every assigned, unscheduled script whose cast overlaps the day's BOOKED talent, best overlap first.
function DayMatchesDialog({ dateStr, canSchedule, onScheduled, onClose }) {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState('');
  const [scheduling, setScheduling] = useState(null);
  const [tick,       setTick]       = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/api/tal/day-matches?date=${dateStr}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e.response?.data?.error || 'The scripts for this day did not load.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateStr, tick]);

  const booked    = data?.booked || [];
  const available = data?.available || [];
  const rows      = data?.rows || [];

  // Per-segment talent: ok = booked this day, danger = assigned but not booked (blocks the segment), plain = open slot.
  const talentChip = (t, i) => (t.talentId
    ? <TalentChip key={t.talentId} name={clean(t.name)} tone={t.booked ? 'ok' : 'danger'}
        title={t.booked ? `${clean(t.name)} is booked this day` : `${clean(t.name)} is assigned but not booked this day`} />
    : <span key={`n${i}`} className="ui-meta" title="No talent assigned to this slot. Assign talent before it can be scheduled.">Unassigned</span>);

  return (
    <>
      <Modal size="medium" title={`Shootable scripts, ${prettyDate(dateStr)}`} onClose={onClose}
        meta={loading ? 'Loading' : booked.length === 0 ? 'No talent booked this day.' : undefined}>
        {!loading && booked.length > 0 && (
          <div className="ui-cluster"><span className="ui-meta">Booked</span>{booked.map(t => <TalentChip key={t.id} name={clean(t.name)} tone="ok" />)}</div>
        )}
        {!loading && available.length > 0 && (
          <div className="ui-cluster"><span className="ui-meta">Available</span>{available.map(t => <TalentChip key={t.id} name={clean(t.name)} tone="warn" />)}</div>
        )}
        {err && <p className="ui-p ui-field__error" role="alert">{clean(err)}</p>}
        {loading ? (
          <div className="ui-stack" role="status" aria-label="Loading the scripts"><Skeleton variant="block" height={72} /><Skeleton variant="block" height={72} /></div>
        ) : rows.length === 0 ? (
          <p className="ui-p">{booked.length === 0
            ? 'Nobody is booked this day yet. Click a gold (available) name on the calendar to book talent, then open this day again.'
            : 'No assigned script has segments this day’s booked talent can shoot.'}</p>
        ) : (
          <div className="ui-stack">
            {rows.map(row => {
              const allAvail = row.availableCount === row.totalUnfilmed;
              return (
                <section key={row.liveProductionId} data-lpid={row.liveProductionId} className="ui-region ui-region--pad ui-stack ui-stack--tight" aria-label={clean(row.title)}>
                  <div className="ui-cluster">
                    <span className="ui-heading ui-truncate">{clean(row.title)}</span>
                    {row.isRiff && <Tag>Riff</Tag>}
                    {row.isSeries && <Tag>Longform</Tag>}
                    {row.isSeries && <span className="ui-meta" data-testid="dm-series-episodes">{plural(row.episodeCount, 'episode')}</span>}
                    {row.client?.name && <span className="ui-meta">{clean(row.client.name)}</span>}
                  </div>
                  {row.segments.map(seg => (
                    <div key={seg.id} className={cx('ui-cluster', !seg.shootable && 'ui-text-3')}>
                      <span className="ui-field__label">{seg.episodeNumber ? `Ep ${seg.episodeNumber}, ${clean(seg.segmentLabel)}` : clean(seg.segmentLabel)}</span>
                      {seg.talents.length === 0 ? talentChip({ talentId: null, name: null }, 0) : seg.talents.map((t, i) => talentChip(t, i))}
                      {seg.shootDay && <span className="ui-meta">Set for {seg.shootDay}{seg.shootTimeSlot ? ` ${fmtTime12(seg.shootTimeSlot)}` : ''}</span>}
                    </div>
                  ))}
                  <div className="ui-cluster">
                    {canSchedule && (
                      <Button variant="primary" icon={CalendarClock} onClick={() => setScheduling(row)}
                        title="Set the shoot day (all day or a time) for the segments shootable on this day">Schedule</Button>
                    )}
                    <span className="ui-spacer" />
                    <Badge tone={allAvail ? 'ok' : 'warn'}>{allAvail ? 'All segments available' : `${row.availableCount} of ${row.totalUnfilmed} segments available`}</Badge>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </Modal>
      {scheduling && (
        <ScheduleShootDialog row={scheduling} dateStr={dateStr} onClose={() => setScheduling(null)}
          onDone={() => { setScheduling(null); setTick(t => t + 1); if (onScheduled) onScheduled(); }} />
      )}
    </>
  );
}

// ── The tab ───────────────────────────────────────────────────────────────────
export default function TalentCalendarView() {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const assignTalent = canAssignTalent(user);
  const now = new Date();
  const [year,            setYear]            = useState(now.getFullYear());
  const [month,           setMonth]           = useState(now.getMonth());
  const [byRange,         setByRange]         = useState({});
  const [loading,         setLoading]         = useState(false);
  const [loadFailed,      setLoadFailed]      = useState(false);
  const [rangeTick,       setRangeTick]       = useState(0);   // retry after a failed month load
  const [selected,        setSelected]        = useState(null);
  const [allTalent,       setAllTalent]       = useState([]);
  const [editingTalentId, setEditingTalentId] = useState('');
  const [editingRows,     setEditingRows]     = useState([]);
  const [editLoading,     setEditLoading]     = useState(false);
  const [modal,           setModal]           = useState(null); // { dateStr, existing }
  const [shootsModal,     setShootsModal]     = useState(null); // { talent, dateStr, segments, loading }
  const [availabilityTalent, setAvailabilityTalent] = useState(null); // { id, name }
  const [segsByDate,     setSegsByDate]     = useState({}); // day → talent on a shoot segment
  const [bookingsByDate, setBookingsByDate] = useState({}); // day → explicit bookings
  const [bookPopover,    setBookPopover]    = useState(null); // { talent: { id, name }, dateStr, bookingId | null }
  const [bookingBusy,    setBookingBusy]    = useState(false);
  const [bookingsTick,   setBookingsTick]   = useState(0);
  const [dayMatches,     setDayMatches]     = useState(null); // 'YYYY-MM-DD' | null
  const [segsTick,       setSegsTick]       = useState(0);
  const [notesByDate,    setNotesByDate]    = useState({});
  const [noteEdit,       setNoteEdit]       = useState(null); // { dateStr, value } | null
  const [noteBusy,       setNoteBusy]       = useState(false);
  const folds = useExpandedDays();

  const openShootsForTalent = useCallback(async (talent, dateStr) => {
    setShootsModal({ talent, dateStr, segments: [], loading: true });
    try {
      const r = await api.get(`/api/shoot-segments?talentId=${talent.id}&shootDay=${dateStr}`);
      setShootsModal({ talent, dateStr, segments: r.data, loading: false });
    } catch {
      setShootsModal({ talent, dateStr, segments: [], loading: false });
    }
  }, []);

  const todayKey = dateKey(now);
  const days = useMemo(() => buildCalDays(year, month), [year, month]);

  useEffect(() => {
    if (!assignTalent) return;   // GET /api/talent: Admin, Power User, Producer
    api.get('/api/talent').then(r => setAllTalent(r.data)).catch(() => {});
  }, [assignTalent]);

  useEffect(() => {
    setLoading(true);
    setLoadFailed(false);
    setSelected(null);
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    api.get(`/api/talent-availability/by-range?from=${dateKey(first)}&to=${dateKey(last)}`)
      .then(r => setByRange(r.data))
      .catch(() => { setByRange({}); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [year, month, rangeTick]);

  // Talent on a shoot segment, per day of the visible month
  useEffect(() => {
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    api.get(`/api/shoot-segments?from=${dateKey(first)}&to=${dateKey(last)}`)
      .then(r => {
        const map = {};
        for (const seg of r.data) {
          const k = seg.shootDay ? seg.shootDay.slice(0, 10) : null;
          if (!k) continue;
          for (const t of (seg.talents || [])) {
            if (!t.talent) continue;
            if (!map[k]) map[k] = new Map();
            if (!map[k].has(t.talent.id)) map[k].set(t.talent.id, { id: t.talent.id, name: t.talent.name, initials: t.talent.initials });
          }
        }
        const out = {};
        for (const [k, m] of Object.entries(map)) out[k] = [...m.values()];
        setSegsByDate(out);
      })
      .catch(() => setSegsByDate({}));
  }, [year, month, segsTick]);

  // Day bookings for the visible month
  useEffect(() => {
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    api.get(`/api/talent-bookings?from=${dateKey(first)}&to=${dateKey(last)}`)
      .then(r => setBookingsByDate(r.data || {}))
      .catch(() => setBookingsByDate({}));
  }, [year, month, bookingsTick]);

  // Producer day notes for the visible grid (it spans the adjacent months)
  useEffect(() => {
    api.get(`/api/tal/day-notes?from=${dateKey(days[0])}&to=${dateKey(days[days.length - 1])}`)
      .then(r => setNotesByDate(r.data || {}))
      .catch(() => setNotesByDate({}));
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveDayNote() {
    if (!noteEdit) return;
    setNoteBusy(true);
    try {
      const r = await api.put(`/api/tal/day-notes/${noteEdit.dateStr}`, { note: noteEdit.value });
      setNotesByDate(prev => {
        const next = { ...prev };
        if (r.data.note) next[noteEdit.dateStr] = r.data.note;
        else delete next[noteEdit.dateStr];
        return next;
      });
      setNoteEdit(null);
    } catch (e) { toast(clean(e.response?.data?.error) || 'The day note did not save. Try again.', { tone: 'danger' }); }
    finally { setNoteBusy(false); }
  }

  async function handleBook() {
    if (!bookPopover) return;
    setBookingBusy(true);
    try {
      await api.post('/api/talent-bookings', { talentId: bookPopover.talent.id, date: bookPopover.dateStr });
      setBookPopover(null);
      setBookingsTick(t => t + 1);
    } catch (e) { toast(clean(e.response?.data?.error) || 'The booking did not save. Try again.', { tone: 'danger' }); }
    finally { setBookingBusy(false); }
  }
  async function handleUnbook() {
    if (!bookPopover?.bookingId) return;
    const ok = await confirm({
      title: 'Remove this booking?',
      body: `${clean(bookPopover.talent.name)} will no longer be booked on ${prettyDate(bookPopover.dateStr)}.`,
      confirmLabel: 'Remove booking',
      destructive: true,
    });
    if (!ok) return;
    setBookingBusy(true);
    try {
      await api.delete(`/api/talent-bookings/${bookPopover.bookingId}`);
      setBookPopover(null);
      setBookingsTick(t => t + 1);
    } catch (e) { toast(clean(e.response?.data?.error) || 'The booking was not removed. Try again.', { tone: 'danger' }); }
    finally { setBookingBusy(false); }
  }

  useEffect(() => {
    if (!editingTalentId) { setEditingRows([]); return; }
    setEditLoading(true);
    api.get(`/api/talent-availability?talentId=${editingTalentId}`)
      .then(r => setEditingRows(r.data))
      .catch(() => setEditingRows([]))
      .finally(() => setEditLoading(false));
  }, [editingTalentId]);

  // The edited talent's rows as a per-day map for the visible month (weekly entries expanded)
  const editingByDate = useMemo(() => {
    if (!editingTalentId || !editingRows.length) return {};
    const map = {};
    for (const day of days) {
      if (day.getMonth() !== month) continue;
      const key = dateKey(day);
      const dow = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate())).getUTCDay();
      for (const row of editingRows) {
        const rowDate = row.date ? row.date.slice(0, 10) : null;
        if (!row.isRecurring) {
          if (rowDate === key) { map[key] = row; break; }
        } else if (row.recurrenceDayOfWeek === dow && rowDate && rowDate <= key && (!row.recurrenceEndDate || row.recurrenceEndDate.slice(0, 10) >= key)) {
          map[key] = row; break;
        }
      }
    }
    return map;
  }, [editingTalentId, editingRows, days, month]);

  function reloadBrowseRange() {
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    api.get(`/api/talent-availability/by-range?from=${dateKey(first)}&to=${dateKey(last)}`).then(r => setByRange(r.data)).catch(() => {});
  }
  function reloadEditingRows() {
    if (!editingTalentId) return;
    api.get(`/api/talent-availability?talentId=${editingTalentId}`).then(r => setEditingRows(r.data)).catch(() => {});
  }

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const isEditMode     = Boolean(editingTalentId);
  const editingName    = isEditMode ? clean(allTalent.find(t => t.id === editingTalentId)?.name || '') : '';
  const selectedAvail  = selected ? (byRange[selected] || []) : [];
  const selectedBookedIds = new Set([
    ...(selected ? (segsByDate[selected] || []).map(t => t.id) : []),
    ...(selected ? (bookingsByDate[selected] || []).map(b => b.talentId) : []),
  ]);

  return (
    <div className="ui-stack ui-stack--loose">
      <div className="ui-toolbar">
        <Button variant="quiet" iconOnly icon={ChevronLeft} label="Previous month" onClick={prevMonth} />
        <span className="ui-heading" aria-live="polite">{MONTH_NAMES[month]} {year}</span>
        <Button variant="quiet" iconOnly icon={ChevronRight} label="Next month" onClick={nextMonth} />
        <Button variant="quiet" onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth()); }}>Today</Button>
        <span className="ui-spacer" />
        {allTalent.length > 0 && (
          <>
            {!isEditMode && (
              <span className="ui-cluster">
                <Badge tone="warn" icon={CalendarClock}>Available</Badge>
                <Badge tone="ok" icon={CalendarCheck}>Booked</Badge>
              </span>
            )}
            <span className="ui-meta">Scheduling for</span>
            <Select aria-label="Scheduling for" value={editingTalentId} onChange={e => { setEditingTalentId(e.target.value); setSelected(null); }}
              options={[{ value: '', label: 'Browse all' }, ...allTalent.map(t => ({ value: t.id, label: clean(t.name) }))]} />
          </>
        )}
      </div>

      {/* Browse mode: the talent pool above the month (names on top, owner call 2026-09-01) */}
      {!isEditMode && !loading && (
        <section className="ui-section" aria-labelledby="tc-pool">
          <div className="ui-section__head">
            <h2 className="ui-section__title" id="tc-pool">Talent</h2>
            <p className="ui-section__note">
              {!selected
                ? 'Click a date to see who is available: gold is available, sage is booked.'
                : `${prettyDate(selected)}: ${selectedAvail.length} of ${allTalent.length} available, ${selectedBookedIds.size} booked`}
            </p>
          </div>
          {allTalent.length === 0 ? (
            <p className="ui-p ui-meta">No talent accounts yet.</p>
          ) : (
            <div className="ui-chip-row">
              {allTalent.map(t => {
                const noColor  = !selected;
                const avEntry  = noColor ? null : selectedAvail.find(av => av.talentId === t.id);
                const isBooked = !noColor && selectedBookedIds.has(t.id);
                const tone = noColor ? undefined : isBooked ? 'ok' : avEntry ? 'warn' : 'danger';
                const tag = isBooked ? 'Booked' : avEntry ? windowOf(avEntry) : '';
                return (
                  <TalentChip key={t.id} name={clean(t.name)} tag={tag} tone={tone}
                    title={assignTalent ? `Edit availability for ${clean(t.name)}` : clean(t.name)}
                    onClick={assignTalent ? () => setAvailabilityTalent({ id: t.id, name: t.name }) : undefined} />
                );
              })}
            </div>
          )}
        </section>
      )}

      {loading ? (
        <Skeleton variant="block" height={480} label="Loading availability" />
      ) : loadFailed ? (
        <ErrorState title="Availability did not load" message="Check your connection, then try again." onRetry={() => setRangeTick(t => t + 1)} />
      ) : (
        <div className="ui-month">
          {DAY_NAMES.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
          {days.map(day => {
            const key     = dateKey(day);
            const otherMo = day.getMonth() !== month;
            if (otherMo) return <CalendarCell key={key} date={key} outside />;

            if (isEditMode) {
              const entry = editingByDate[key];
              return (
                <CalendarCell key={key} date={key} today={key === todayKey} data-tone={entry ? 'warn' : undefined}
                  items={entry ? [{ id: entry.id, label: `${windowOf(entry)}${entry.isRecurring ? ', weekly' : ''}` }] : []}
                  aria-label={`${prettyDate(key)}: ${entry ? `${windowOf(entry)}, edit` : 'add availability'}`}
                  onClick={() => setModal({ dateStr: key, existing: entry || null })} />
              );
            }

            // Browse: shooting and booked talent (ok), then available and not booked (warn)
            const availEntries = byRange[key] || [];
            const schedTalents = segsByDate[key] || [];
            const dayBookings  = bookingsByDate[key] || [];
            const schedIds     = new Set(schedTalents.map(t => t.id));
            const bookedIds    = new Set([...schedIds, ...dayBookings.map(b => b.talentId)]);
            const goldEntries  = availEntries.filter(av => !bookedIds.has(av.talentId));
            const combined = [
              ...schedTalents.map(t => ({ kind: 'shooting', id: `s-${t.id}`, talent: t })),
              ...dayBookings.filter(b => !schedIds.has(b.talentId)).map(b => ({ kind: 'booked', id: `b-${b.talentId}`, booking: b })),
              ...goldEntries.map(av => ({ kind: 'available', id: av.id || `a-${av.talentId}`, av })),
            ];
            const isSel = selected === key;
            const note = notesByDate[key];
            const day0 = prettyDate(key);
            const items = combined.length > MAX_TALENT_CHIPS
              ? (folds.has(key) ? [...combined, { id: '__less', fold: 'less' }] : [...combined.slice(0, MAX_TALENT_CHIPS), { id: '__more', fold: 'more', n: combined.length - MAX_TALENT_CHIPS }])
              : combined;
            return (
              <CalendarCell key={key} date={key} today={key === todayKey} items={items} max={Infinity}
                aria-selected={isSel} tabIndex={0}
                aria-label={`${day0}${combined.length ? `, ${plural(combined.length, 'talent')}` : ''}${note ? `. Note: ${clean(note)}` : ''}`}
                title="Double-click: scripts this day's booked talent can shoot"
                onClickCapture={cellClick(() => setSelected(isSel ? null : key))}
                onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); setSelected(isSel ? null : key); } }}
                onDoubleClick={cellClick(() => { setSelected(key); setDayMatches(key); })}
                action={(
                  <span className="ui-cluster ui-cluster--nowrap ui-grow">
                    {note && (assignTalent
                      ? <button type="button" className="ui-link ui-meta ui-clamp" title={`${clean(note)}. Click to edit the day note.`}
                          onClick={e => { e.stopPropagation(); setNoteEdit({ dateStr: key, value: note }); }}>{clean(note)}</button>
                      : <span className="ui-meta ui-clamp" title={clean(note)}>{clean(note)}</span>)}
                    <span className="ui-spacer" />
                    {!note && assignTalent && (
                      <Button variant="quiet" iconOnly icon={StickyNote} label={`Add a day note for ${day0}: what shoots this day`}
                        onClick={e => { e.stopPropagation(); setNoteEdit({ dateStr: key, value: '' }); }} />
                    )}
                    <Button variant="quiet" iconOnly icon={ListChecks} label={`Scripts the talent booked on ${day0} can shoot`}
                      onClick={e => { e.stopPropagation(); setSelected(key); setDayMatches(key); }} />
                  </span>
                )}
                renderItem={item => {
                  if (item.fold) return <FoldControl item={item} total={combined.length} onExpand={() => folds.expand(key)} onCollapse={() => folds.collapse(key)} />;
                  if (item.kind === 'shooting') {
                    const t = item.talent;
                    return <TalentChip name={clean(t.name)} tag="Shooting" tone="ok" title={`${clean(t.name)} is booked on a shoot. Click to see which.`} onClick={() => openShootsForTalent(t, key)} />;
                  }
                  if (item.kind === 'booked') {
                    const b = item.booking;
                    return (
                      <TalentChip name={clean(b.name)} tag="Booked" tone="ok"
                        title={assignTalent ? `${clean(b.name)} is booked to work this day. Click to manage the booking.` : `${clean(b.name)} is booked to work this day`}
                        onClick={assignTalent ? () => setBookPopover({ talent: { id: b.talentId, name: b.name }, dateStr: key, bookingId: b.id }) : undefined} />
                    );
                  }
                  const av  = item.av;
                  const tal = allTalent.find(t => t.id === av.talentId);
                  const name = clean(tal?.name || 'Unknown');
                  const when = windowOf(av);
                  return (
                    <TalentChip name={name} tag={when} tone="warn"
                      title={assignTalent ? `${name} is available (${when}). Click to book them for this day.` : `${name} is available (${when})`}
                      onClick={tal ? () => {
                        if (assignTalent) setBookPopover({ talent: { id: tal.id, name: tal.name }, dateStr: key, bookingId: null });
                        else openShootsForTalent(tal, key);
                      } : undefined} />
                  );
                }} />
            );
          })}
        </div>
      )}

      {isEditMode && !loading && (
        <div className="ui-region ui-region--pad ui-cluster" role="status">
          <Icon icon={Pencil} />
          <span>{editLoading ? `Loading ${editingName}'s availability.` : `Editing ${editingName}. Click any date to add or edit availability.`}</span>
        </div>
      )}

      {shootsModal && (
        <TalentShootsDialog talent={shootsModal.talent} dateStr={shootsModal.dateStr} segments={shootsModal.segments}
          loading={shootsModal.loading} onClose={() => setShootsModal(null)} />
      )}

      {dayMatches && (
        <DayMatchesDialog dateStr={dayMatches} canSchedule={assignTalent} onScheduled={() => setSegsTick(t => t + 1)} onClose={() => setDayMatches(null)} />
      )}

      {/* The producer's day note ("theme of the day") */}
      {noteEdit && (
        <Modal title="Day note" meta={prettyDate(noteEdit.dateStr)} onClose={() => setNoteEdit(null)}
          footer={(
            <>
              <span className="ui-meta">An empty note removes it.</span>
              <span className="ui-spacer" />
              <Button variant="quiet" onClick={() => setNoteEdit(null)}>Cancel</Button>
              <Button variant="primary" onClick={saveDayNote} loading={noteBusy}>Save note</Button>
            </>
          )}>
          <Textarea label="What shoots this day?" hint="Everyone who opens this calendar sees it, for example: PG today, or TPK and PG."
            data-autofocus rows={3} maxLength={500} value={noteEdit.value}
            onChange={e => setNoteEdit({ ...noteEdit, value: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveDayNote(); }} />
        </Modal>
      )}

      {/* Book / unbook, from a gold (available) or sage (booked) name on a day */}
      {bookPopover && (
        <Modal title={clean(bookPopover.talent.name)} meta={prettyDate(bookPopover.dateStr)} onClose={() => setBookPopover(null)}
          footer={(
            <>
              <Button variant="quiet" onClick={() => setBookPopover(null)}>Cancel</Button>
              {bookPopover.bookingId
                ? <Button variant="danger" icon={Trash2} onClick={handleUnbook} loading={bookingBusy}>Remove booking</Button>
                : <Button variant="primary" icon={CalendarCheck} onClick={handleBook} loading={bookingBusy}>Book</Button>}
            </>
          )}>
          <p className="ui-p">{bookPopover.bookingId ? 'Booked to work this day.' : 'Available this day. Book them to lock the day in.'}</p>
        </Modal>
      )}

      {modal && (
        <AvailabilityModal strictTimes dateStr={modal.dateStr} existing={modal.existing} talentId={editingTalentId}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); reloadEditingRows(); reloadBrowseRange(); }}
          onDeleted={() => { setModal(null); reloadEditingRows(); reloadBrowseRange(); }} />
      )}

      {availabilityTalent && (
        <Modal size="wide" title={`Availability: ${clean(availabilityTalent.name)}`} meta="Click any date to mark or edit availability for this talent."
          onClose={() => { setAvailabilityTalent(null); reloadBrowseRange(); }}>
          <TalentAvailabilityView talentId={availabilityTalent.id} />
        </Modal>
      )}
    </div>
  );
}

